import { useState, useEffect, useMemo } from 'react'
import './SchoolEmployeesEditor.css'
import SchoolEmployeeEditModal, { SchoolEmployeeFormData } from './SchoolEmployeeEditModal'
import { useAuth } from '../hooks/useAuth'
import {
  formatBirthDateDisplay,
  formatEmployeeFullName,
  getEmployeeInitials,
  getEmployeePositionLabel,
} from '../utils/employeeDisplay'
import { comparePersonNames } from '../utils/personNameSort'

export type SchoolEmployeesEditorProps = {
  requireUnlockForMutations?: boolean
}

interface Employee {
  id: number
  name: string
  title: string | null
  first_name: string | null
  last_name: string | null
  title_after: string | null
  email: string | null
  work_email: string | null
  personal_number: string | null
  birth_date: string | null
  phone: string | null
  position: string | null
  linked_user_email?: string | null
  user_id?: string | null
  is_active?: boolean
}

function employeeToForm(employee: Employee): SchoolEmployeeFormData {
  return {
    id: employee.id,
    title: employee.title || '',
    first_name: employee.first_name || '',
    last_name: employee.last_name || '',
    title_after: employee.title_after || '',
    work_email: employee.work_email || '',
    personal_number: employee.personal_number || '',
    birth_date: employee.birth_date || '',
    phone: employee.phone || '',
    position: employee.position || '',
    is_active: employee.is_active !== false,
  }
}

export default function SchoolEmployeesEditor({
  requireUnlockForMutations = false,
}: SchoolEmployeesEditorProps) {
  const { user } = useAuth()
  const isItAdmin = user?.role === 'admin' && user?.admin_type === 'spravce_it'
  const [employees, setEmployees] = useState<Employee[]>([])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [mutationsUnlocked, setMutationsUnlocked] = useState(!requireUnlockForMutations)
  const [searchTerm, setSearchTerm] = useState('')
  const [statusFilter, setStatusFilter] = useState<'active' | 'passive' | 'all'>('active')
  const [editModalOpen, setEditModalOpen] = useState(false)
  const [editModalMode, setEditModalMode] = useState<'create' | 'edit'>('edit')
  const [editModalInitial, setEditModalInitial] = useState<Partial<SchoolEmployeeFormData> | null>(null)
  const [editLinkedEmail, setEditLinkedEmail] = useState<string | null>(null)

  useEffect(() => {
    loadEmployees()
  }, [])

  useEffect(() => {
    setMutationsUnlocked(!requireUnlockForMutations)
  }, [requireUnlockForMutations])

  const loadEmployees = async () => {
    try {
      setLoading(true)
      const apiUrl = window.location.origin + '/api'
      const response = await fetch(`${apiUrl}/employees`, {
        credentials: 'include',
      })

      if (response.ok) {
        const data = await response.json()
        setEmployees(Array.isArray(data) ? data : [])
      }
    } catch (error) {
      console.error('Error loading employees:', error)
    } finally {
      setLoading(false)
    }
  }

  const showMutateUi = mutationsUnlocked

  const openCreateModal = () => {
    setEditModalMode('create')
    setEditModalInitial(null)
    setEditLinkedEmail(null)
    setEditModalOpen(true)
  }

  const openEditModal = (employee: Employee) => {
    setEditModalMode('edit')
    setEditModalInitial(employeeToForm(employee))
    setEditLinkedEmail(employee.linked_user_email || null)
    setEditModalOpen(true)
  }

  const closeEditModal = () => {
    if (saving) return
    setEditModalOpen(false)
    setEditModalInitial(null)
    setEditLinkedEmail(null)
  }

  const handleSaveForm = async (form: SchoolEmployeeFormData) => {
    try {
      setSaving(true)
      const apiUrl = window.location.origin + '/api'
      const payload = {
        title: form.title || null,
        first_name: form.first_name || null,
        last_name: form.last_name || null,
        title_after: form.title_after || null,
        work_email: form.work_email || null,
        personal_number: form.personal_number || null,
        birth_date: form.birth_date || null,
        phone: form.phone || null,
        position: form.position || null,
        is_active: form.is_active,
      }

      const response = await fetch(`${apiUrl}/employees`, {
        method: editModalMode === 'create' ? 'POST' : 'PUT',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(
          editModalMode === 'create' ? payload : { id: form.id, ...payload },
        ),
      })

      if (response.ok) {
        await loadEmployees()
        closeEditModal()
      } else {
        const error = await response.json()
        alert('Chyba: ' + (error.error || 'Neznámá chyba'))
      }
    } catch (error) {
      console.error('Error saving employee:', error)
      alert('Chyba při ukládání zaměstnance')
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async (id: number, name: string) => {
    if (!confirm(`Opravdu chcete smazat zaměstnance "${name}"?`)) return

    try {
      const apiUrl = window.location.origin + '/api'
      const response = await fetch(`${apiUrl}/employees`, {
        method: 'DELETE',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ id }),
      })

      if (response.ok) {
        await loadEmployees()
      } else {
        const error = await response.json()
        alert('Chyba při mazání: ' + (error.error || 'Neznámá chyba'))
      }
    } catch (error) {
      console.error('Error deleting employee:', error)
      alert('Chyba při mazání zaměstnance')
    }
  }

  const toggleMutationsUnlocked = () => {
    if (mutationsUnlocked) {
      if (editModalOpen) {
        if (!window.confirm('Máte rozpracované změny. Vypnout režim úprav?')) return
        closeEditModal()
      }
      setMutationsUnlocked(false)
    } else {
      setMutationsUnlocked(true)
    }
  }

  const toggleActive = async (employee: Employee) => {
    const next = employee.is_active === false
    const label = next ? 'aktivovat' : 'deaktivovat (pasivní)'
    if (!window.confirm(`Opravdu ${label} zaměstnance „${employee.name}"?`)) return
    try {
      setSaving(true)
      const apiUrl = window.location.origin + '/api'
      const response = await fetch(`${apiUrl}/employees`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id: employee.id,
          title: employee.title,
          first_name: employee.first_name,
          last_name: employee.last_name,
          title_after: employee.title_after,
          work_email: employee.work_email,
          personal_number: employee.personal_number,
          birth_date: employee.birth_date,
          phone: employee.phone,
          position: employee.position,
          is_active: next,
        }),
      })
      if (response.ok) {
        await loadEmployees()
      } else {
        const error = await response.json()
        alert('Chyba: ' + (error.error || 'Neznámá chyba'))
      }
    } catch (error) {
      console.error('Error toggling employee active:', error)
      alert('Chyba při změně stavu')
    } finally {
      setSaving(false)
    }
  }

  const filteredEmployees = useMemo(() => {
    const searchLower = searchTerm.toLowerCase()
    const list = employees.filter((emp) => {
      const active = emp.is_active !== false
      if (statusFilter === 'active' && !active) return false
      if (statusFilter === 'passive' && active) return false
      if (!searchLower) return true
      return (
        (emp.name && emp.name.toLowerCase().includes(searchLower)) ||
        (emp.first_name && emp.first_name.toLowerCase().includes(searchLower)) ||
        (emp.last_name && emp.last_name.toLowerCase().includes(searchLower)) ||
        (emp.work_email && emp.work_email.toLowerCase().includes(searchLower)) ||
        (emp.personal_number && emp.personal_number.includes(searchTerm)) ||
        (emp.phone && emp.phone.includes(searchTerm)) ||
        (emp.position && emp.position.toLowerCase().includes(searchLower))
      )
    })
    list.sort((a, b) =>
      comparePersonNames(
        { name: a.name, first_name: a.first_name, last_name: a.last_name },
        { name: b.name, first_name: b.first_name, last_name: b.last_name },
      ),
    )
    return list
  }, [employees, searchTerm, statusFilter])

  if (loading) {
    return <div className="loading">Načítání...</div>
  }

  return (
    <div className="school-employees-editor">
      <div className="editor-header">
        <div>
          <h2>Správa zaměstnanců školy</h2>
          <p className="editor-subtitle">Klikněte na řádek nebo Upravit — údaje se mění v dialogu</p>
        </div>
        <span className="employees-count-badge">
          {filteredEmployees.length} / {employees.length} záznamů
        </span>
      </div>

      {requireUnlockForMutations && (
        <div className={`editor-mutations-banner ${mutationsUnlocked ? 'editor-mutations-banner--unlocked' : ''}`}>
          <p className="editor-mutations-banner__text">
            {mutationsUnlocked
              ? 'Režim úprav je zapnutý.'
              : 'Pro přidání, úpravu nebo smazání zapněte režim úprav.'}
          </p>
          <button type="button" onClick={toggleMutationsUnlocked} className="editor-mutations-toggle">
            {mutationsUnlocked ? 'Vypnout režim úprav' : 'Zapnout režim úprav'}
          </button>
        </div>
      )}

      <div className="editor-controls">
        <div className="editor-search-wrap">
          <span className="editor-search-icon" aria-hidden>⌕</span>
          <input
            type="search"
            placeholder="Hledat jméno, e-mail, tel. nebo os. č.…"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
        </div>
        <label className="editor-status-filter">
          Stav
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as 'active' | 'passive' | 'all')}
          >
            <option value="active">Aktivní</option>
            <option value="passive">Pasivní</option>
            <option value="all">Všichni</option>
          </select>
        </label>
        <button type="button" onClick={loadEmployees} className="refresh-btn">
          Obnovit
        </button>
        {showMutateUi && (
          <button type="button" onClick={openCreateModal} className="add-btn">
            Přidat zaměstnance
          </button>
        )}
      </div>

      <div className="editor-table-container">
        <div className="editor-table-scroll">
          <table className="editor-compact-table">
            <thead>
              <tr>
                <th className="col-avatar" aria-hidden />
                <th>Zaměstnanec</th>
                <th className="col-os">Os. č.</th>
                <th className="col-phone">Telefon</th>
                <th className="col-work">Prac. e-mail</th>
                <th className="col-status">Stav</th>
                {showMutateUi && <th className="col-actions">Akce</th>}
              </tr>
            </thead>
            <tbody>
              {filteredEmployees.map((employee) => {
                const fullName = formatEmployeeFullName(employee)
                const positionLabel = getEmployeePositionLabel(employee)
                const initials = getEmployeeInitials(employee)
                const birthLabel = employee.birth_date
                  ? formatBirthDateDisplay(employee.birth_date)
                  : null
                const isActive = employee.is_active !== false

                return (
                  <tr
                    key={employee.id}
                    className={[
                      showMutateUi ? 'editor-row--clickable' : '',
                      isActive ? '' : 'editor-row--passive',
                    ]
                      .filter(Boolean)
                      .join(' ') || undefined}
                    onClick={showMutateUi ? () => openEditModal(employee) : undefined}
                  >
                    <td className="col-avatar">
                      <span className="editor-avatar" aria-hidden>{initials}</span>
                    </td>
                    <td className="editor-name-cell">
                      <span className="editor-name">{fullName}</span>
                      {positionLabel && (
                        <span className="editor-position-sub" title={positionLabel}>{positionLabel}</span>
                      )}
                      {birthLabel && (
                        <span className="editor-birth-sub">Nar. {birthLabel}</span>
                      )}
                    </td>
                    <td className="col-os">{employee.personal_number || '—'}</td>
                    <td className="col-phone">{employee.phone || '—'}</td>
                    <td className="col-work">
                      {employee.work_email ? (
                        <a
                          href={`mailto:${employee.work_email}`}
                          className="editor-email-link"
                          title={employee.work_email}
                          onClick={(e) => e.stopPropagation()}
                        >
                          {employee.work_email}
                        </a>
                      ) : (
                        '—'
                      )}
                    </td>
                    <td className="col-status">
                      <span
                        className={
                          isActive ? 'editor-status-pill editor-status-pill--on' : 'editor-status-pill editor-status-pill--off'
                        }
                      >
                        {isActive ? 'Aktivní' : 'Pasivní'}
                      </span>
                    </td>
                    {showMutateUi && (
                      <td className="col-actions" onClick={(e) => e.stopPropagation()}>
                        <div className="editor-row-actions">
                          <button type="button" className="edit-btn" onClick={() => openEditModal(employee)}>
                            Upravit
                          </button>
                          <button
                            type="button"
                            className="edit-btn"
                            disabled={saving}
                            onClick={() => void toggleActive(employee)}
                          >
                            {isActive ? 'Pasivní' : 'Aktivovat'}
                          </button>
                          <button
                            type="button"
                            className="delete-btn"
                            onClick={() => handleDelete(employee.id, employee.name)}
                          >
                            Smazat
                          </button>
                        </div>
                      </td>
                    )}
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      </div>

      <SchoolEmployeeEditModal
        isOpen={editModalOpen}
        mode={editModalMode}
        initial={editModalInitial}
        saving={saving}
        showItLinkLogin={!!isItAdmin}
        linkedUserEmail={editLinkedEmail}
        onClose={closeEditModal}
        onSave={handleSaveForm}
      />
    </div>
  )
}
